import { useState, useEffect } from 'react';
import { singletonHook } from 'react-singleton-hook';
import {
  getBrowserFeature,
  VirtualKeyboardGeometryChangeEvent,
} from '../getBrowserFeature';
import { listenForViewportChange } from './listenForViewportChange';
import { Bounds, getWindowBounds } from './getWindowBounds';

const hookInitValues = getWindowBounds();
const { virtualKeyboard } = getBrowserFeature();

/**
 * A React hook to get the current window bounds.
 * It updates the bounds when the window is resized or when the virtual keyboard's geometry changes.
 * The bounds are expected to change when:
 * 1. The window is resized (width or height)
 * 2. The virtual keyboard is displayed, hidden, or resized (if supported)
 *
 * @returns {Bounds} An object containing the following properties:
 * - width: {number} The current window avaiable width
 * - height: {number} The current window avaiable height
 */
export const useBoundsImpl = (keyboard = virtualKeyboard): Bounds => {
  const [bounds, setBounds] = useState<Bounds>(hookInitValues);
  const updateBounds = (): void => {
    setBounds(getWindowBounds());
  };

  const updateBoundsOnGeometryChange = (
    event: VirtualKeyboardGeometryChangeEvent
  ): void => {
    const virtualKeyboardHeight = event.target.boundingRect.height;
    const { height, width } = getWindowBounds();
    setBounds({
      height: height - virtualKeyboardHeight,
      width: width,
    });
  };

  const { addListener, removeListener } = listenForViewportChange(updateBounds);

  useEffect(() => {
    updateBounds();
    addListener();
    keyboard?.addEventListener('geometrychange', updateBoundsOnGeometryChange);

    return (): void => {
      removeListener();
      keyboard?.removeEventListener(
        'geometrychange',
        updateBoundsOnGeometryChange
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return bounds;
};

export const useBounds = singletonHook<Bounds>(hookInitValues, useBoundsImpl);
export { Bounds };
